import { getUniqueDirectoriesPaths } from 'helpers/getUniqueDirectoriesPaths';
import { DirectoriesBatchCreatedModel } from 'shared/domain/directory/directoryModel';
import { buildDirectoriesBatchCreateModel } from 'shared/domain/directory/mapping';
import { isNotSystemFile } from 'helpers/fileFilters';
import {
  showGenericErrorToast,
  showToast,
} from 'services/toaster';
import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { debugLog } from 'shared/logger/debugLog';
import { isChannelRequestTimeout } from 'shared/channels/errors';
import { getErrorMessage } from 'shared/utils/errorHandling';
import { Progress, useUploadProgressStore } from 'store/uploadProgress';
import { UploadConfig } from './types';
import {
  createDirectoriesBatch,
  getAuthor,
  getDirectoryPath,
  readFilesWithPlaceholders,
  registerDirectoryCreationPlaceholder,
  registerUploadPlaceholders,
  resolveUniqueId,
  saveLocally,
  showErrors,
  toSavedLocalIds,
  sortFilesByFileName,
} from './model';

export type { UploadConfig } from './types';

export async function uploadDocumentations(
  files: File[],
  targetDirectoryId: string,
  config: UploadConfig
): Promise<{ createdDocumentationLocalIds: number[] }> {
  const uniqueIdByFile = registerUploadPlaceholders(files);
  const { successfullFiles, failedFiles } = await readFilesWithPlaceholders(
    files,
    uniqueIdByFile
  );
  const author = getAuthor(config);

  const createdDocumentationLocalIds = toSavedLocalIds(
    await Promise.all(
      successfullFiles.map((file) =>
        saveLocally(
          file,
          author,
          targetDirectoryId,
          resolveUniqueId(file, uniqueIdByFile)
        )
      )
    )
  );

  showErrors(failedFiles);

  return { createdDocumentationLocalIds };
}

export async function uploadDirectoriesWithFiles(
  files: File[],
  targetDirectoryId: string,
  config: UploadConfig,
  emptyDirectoryPaths: string[] = []
): Promise<{ createdDirectoryIds: string[]; createdDocumentationLocalIds: number[] }> {
  const { updateFileWhere, removeFile, markUploadPlaceholderFailed } =
    useUploadProgressStore.getState();

  const uniqueDirectoryPaths = Array.from(
    new Set([...getUniqueDirectoriesPaths(files), ...emptyDirectoryPaths])
  );

  let directoryIdMap: Record<string, string> = {};
  const createdDirectoryIds: string[] = [];

  const batchModel = buildDirectoriesBatchCreateModel(
    uniqueDirectoryPaths,
    targetDirectoryId
  );

  const sortedFiles = files.filter(isNotSystemFile).sort(sortFilesByFileName);

  // File placeholders are only registered once the folder step is done, so
  // the panel shows "Creating folders..." first, then swaps to the file list.
  const directoryPlaceholderId =
    batchModel.directories.length > 0
      ? registerDirectoryCreationPlaceholder()
      : undefined;

  if (directoryPlaceholderId) {
    let result: DirectoriesBatchCreatedModel;
    try {
      result = await createDirectoriesBatch(batchModel);
    } catch (error) {
      updateFileWhere(
        (file) => file.uniqueFileId === directoryPlaceholderId,
        { status: Progress.FAILED }
      );

      // A timeout's message is technical (the channel and its method): the generic toast says it better.
      const message = isChannelRequestTimeout(error) ? undefined : getErrorMessage(error);
      if (message) {
        showToast({ type: TOASTER_TYPES.FAILURE, message });
      } else {
        showGenericErrorToast();
      }
      return { createdDirectoryIds: [], createdDocumentationLocalIds: [] };
    }

    removeFile(directoryPlaceholderId);

    debugLog('[FolderUpload] batch response:', result.directories);

    for (const dir of result.directories) {
      if (dir.path && dir._id) {
        directoryIdMap[dir.path] = dir._id;
        createdDirectoryIds.push(dir._id);
      }
    }
  }

  debugLog('[FolderUpload] directoryIdMap:', directoryIdMap);

  const uniqueIdByFile = registerUploadPlaceholders(sortedFiles);
  const { successfullFiles, failedFiles } = await readFilesWithPlaceholders(
    sortedFiles,
    uniqueIdByFile
  );
  const author = getAuthor(config);

  const createdDocumentationLocalIds = await Promise.all(
    successfullFiles.map(async (file) => {
      const directoryPath = getDirectoryPath(file);
      const uniqueId = resolveUniqueId(file, uniqueIdByFile);

      if (directoryPath) {
        const directoryId = directoryIdMap[directoryPath];
        if (!directoryId) {
          debugLog(
            '[FolderUpload] SKIPPED - no mapping for path:',
            directoryPath,
            'available keys:',
            Object.keys(directoryIdMap)
          );
          if (uniqueId) markUploadPlaceholderFailed(uniqueId);
          return null;
        }
        return saveLocally(file, author, directoryId, uniqueId);
      }

      return saveLocally(file, author, targetDirectoryId, uniqueId);
    })
  );

  showErrors(failedFiles);

  return {
    createdDirectoryIds,
    createdDocumentationLocalIds: toSavedLocalIds(createdDocumentationLocalIds),
  };
}
